/**
 * NotificationBell — small sidebar/header widget that shows the agent's
 * unread notifications at a glance. Merges TWO sources:
 *   • /api/analytics/notifications/mine  — legacy manager-reassignment notes
 *   • /api/tfvf/notifications             — TFVF training/coaching notes
 *     (training_assigned, assessment_failed, practical_signoff,
 *      certification_issued, retraining_assigned)
 *
 * Clicking a TFVF note deep-links to the relevant training screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, ArrowRight, GraduationCap, Award, Zap, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

const TFVF_META = {
  training_assigned:     { icon: GraduationCap, label: 'Training assigned',     to: (n) => n.module_id ? `/training/${n.module_id}` : '/training' },
  assessment_failed:     { icon: AlertTriangle, label: 'Quiz not passed',       to: (n) => n.module_id ? `/training/${n.module_id}` : '/training' },
  practical_signoff:     { icon: ShieldCheck,   label: 'Practical signed off',  to: () => '/training/certification' },
  certification_issued:  { icon: Award,         label: 'Certificate issued',    to: () => '/training/certification' },
  retraining_assigned:   { icon: Zap,           label: 'Retraining assigned',   to: () => '/training/retraining' },
};

export const NotificationBell = ({ compact = false }) => {
  const [notes, setNotes] = useState([]);              // legacy manager reassignments
  const [tfvfNotes, setTfvfNotes] = useState([]);      // TFVF training notes
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/analytics/notifications/mine?limit=8`).catch(() => ({ data: {} })),
      axios.get(`${API}/tfvf/notifications`).catch(() => ({ data: { rows: [] } })),
    ]).then(([legacy, tfvf]) => {
      const legacyList = legacy.data?.notifications || [];
      const tfvfList = (tfvf.data?.rows || []).slice(0, 8);
      setNotes(legacyList);
      setTfvfNotes(tfvfList);
      const tfvfUnread = tfvfList.filter(n => !n.seen).length;
      setUnread((legacy.data?.unread_count || 0) + tfvfUnread);
    });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/analytics/notifications/mark-read?all_flag=true`);
      // Mark all TFVF notes as seen in parallel.
      await Promise.all(
        tfvfNotes.filter(n => !n.seen).map(n =>
          axios.post(`${API}/tfvf/notifications/${n.id}/seen`).catch(() => {}),
        ),
      );
      setNotes(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
      setTfvfNotes(prev => prev.map(n => ({ ...n, seen: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const openLegacyNote = async (n) => {
    setOpen(false);
    try {
      await axios.post(`${API}/analytics/notifications/mark-read?call_id=${encodeURIComponent(n.call_id)}`);
    } catch { /* silent */ }
    if (n.type === 'training_push') {
      navigate(n.link || '/training');
    } else {
      navigate(`/calls/${n.call_id}`);
    }
    load();
  };

  const openTfvfNote = async (n) => {
    setOpen(false);
    try { await axios.post(`${API}/tfvf/notifications/${n.id}/seen`); } catch { /* silent */ }
    const meta = TFVF_META[n.type];
    if (meta) navigate(meta.to(n));
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors ${compact ? 'w-9 h-9' : 'w-full justify-start px-3 py-2'}`}
          data-testid="notif-bell"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        >
          <Bell className={compact ? 'w-4 h-4' : 'w-4 h-4 mr-2'} />
          {!compact && <span className="text-sm">Notifications</span>}
          {unread > 0 && (
            <span
              className={`absolute inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold text-white bg-rose-600 rounded-full shadow ${compact ? '-top-1 -right-1' : 'top-1 right-2'}`}
              data-testid="notif-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-popover">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Notifications {unread > 0 && <span className="text-rose-600">· {unread} new</span>}
          </p>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-[11px] font-semibold text-teal-700 hover:text-teal-900"
              data-testid="notif-mark-all-read"
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {notes.length === 0 && tfvfNotes.length === 0 && (
            <p className="text-xs text-slate-400 italic px-3 py-6 text-center">
              You&apos;re all caught up.
            </p>
          )}
          {/* TFVF training notes first — usually more actionable */}
          {tfvfNotes.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {tfvfNotes.map((n) => {
                const meta = TFVF_META[n.type] || { icon: Bell, label: n.type };
                const Icon = meta.icon;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openTfvfNote(n)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${n.seen ? '' : 'bg-teal-50/50'}`}
                      data-testid={`notif-tfvf-${n.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {!n.seen && <span className="w-1.5 h-1.5 rounded-full bg-teal-600 shrink-0" />}
                        <Icon className="w-3 h-3 text-teal-700 shrink-0" />
                        <span className="text-xs font-semibold text-slate-800 truncate flex-1">
                          {meta.label}
                        </span>
                        <span className="text-[9px] uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1 py-0.5">
                          TFVF
                        </span>
                      </div>
                      {(n.module_id || n.criterion) && (
                        <p className="text-[11px] text-teal-800 italic mt-1 truncate">
                          {n.module_id || n.criterion}
                          {n.due_date && <> · due {n.due_date}</>}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                        {new Date(n.at).toLocaleString()}
                        <ArrowRight className="w-2.5 h-2.5 ml-auto" />
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {/* Legacy manager reassignments */}
          <ul className="divide-y divide-slate-100">
            {notes.map((n) => (
              <li key={`${n.call_id}-${n.created_at}`}>
                <button
                  type="button"
                  onClick={() => openLegacyNote(n)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${n.read_at ? '' : 'bg-purple-50/40'}`}
                  data-testid={`notif-item-${n.call_id}`}
                >
                  <div className="flex items-center gap-2">
                    {!n.read_at && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
                    )}
                    <span className="text-xs font-mono font-semibold text-slate-800 truncate flex-1">
                      {n.type === 'training_push'
                        ? 'Training from your supervisor'
                        : n.customer_phone || (n.type === 'sms_reply' ? 'SMS reply' : 'Manager reassignment')}
                    </span>
                    {n.type === 'training_push' && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 rounded px-1 py-0.5" data-testid="notif-training-chip">
                        Training
                      </span>
                    )}
                    {n.type === 'sms_reply' && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 rounded px-1 py-0.5" data-testid="notif-sms-reply-chip">
                        Reply
                      </span>
                    )}
                    {n.caller_disc && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1 py-0.5">
                        {n.caller_disc}
                      </span>
                    )}
                  </div>
                  {n.note && (
                    <p className={`text-[11px] italic mt-1 line-clamp-2 ${n.type === 'sms_reply' ? 'text-emerald-700' : 'text-purple-700'}`}>
                      &ldquo;{n.note}&rdquo;
                    </p>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                    {n.type === 'sms_reply' ? 'SMS reply received' : `from ${n.from_manager_email?.split('@')[0] || 'manager'}`}
                    <ArrowRight className="w-2.5 h-2.5 ml-auto" />
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
